import React, { useEffect, useState } from 'react';
import { sendApiRequest } from '../../utils';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { FiFileText, FiEye } from 'react-icons/fi';

const ShowAllPackageApproval = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [receipts, setReceipts] = useState({});

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await sendApiRequest('api/packages/admin/all-requests', {}, 'GET');
      
      if (response.data.success) {
        setRequests(response.data.data);
        // Fetch receipts for all requests
        const receiptsMap = {};
        for (const request of response.data.data) {
          try {
            const receiptResponse = await sendApiRequest(`api/packages/admin/get-receipt/${request._id}`, {}, 'GET');
            if (receiptResponse.data.success && receiptResponse.data.data) {
              receiptsMap[request._id] = receiptResponse.data.data;
            }
          } catch (error) {
            console.error('Error fetching receipt:', error);
          }
        }
        setReceipts(receiptsMap);
      } else {
        toast.error(response.data.message || 'Failed to fetch package requests');
      }
    } catch (error) {
      console.error('Error fetching package requests:', error);
      toast.error('Failed to fetch package requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      setUpdating(true);
      const response = await sendApiRequest(
        `api/packages/admin/update-status/${requestId}`,
        { status: newStatus },
        'PUT'
      );
      
      if (response.data.success) {
        toast.success('Status updated successfully');
        // Update the local state
        setRequests(prevRequests =>
          prevRequests.map(request =>
            request._id === requestId
              ? { ...request, status: newStatus }
              : request
          )
        );
      } else {
        toast.error(response.data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#49c2c7]/5 via-[#25b2da]/10 to-[#0d61b5]/5 py-8">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-[#25b2da]/20">
          <div className="bg-gradient-to-r from-[#13407e] to-[#0d61b5] text-white px-6 py-4">
            <h1 className="text-xl font-semibold">Package Requests</h1>
          </div>

          <div className="p-6">
            {requests.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Agent Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Package Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Purchase Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {requests.map((request) => (
                      <tr key={request._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {request.user?.fullName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.user?.email}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.user?.phone}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {request.user?.sponsorCode}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {request.customer?.fullName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.customer?.email}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.customer?.phone}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {request.package?.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            ₹{request.package?.price?.toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.package?.width} x {request.package?.length}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(request.purchaseDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded ${
                            request.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : request.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-col space-y-2">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleStatusChange(request._id, 'pending')}
                                disabled={request.status === 'pending' || updating}
                                className={`px-2 py-1 text-xs rounded ${
                                  request.status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800 cursor-not-allowed'
                                    : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                }`}
                              >
                                Pending
                              </button>
                              <button
                                onClick={() => handleStatusChange(request._id, 'active')}
                                disabled={request.status === 'active' || updating}
                                className={`px-2 py-1 text-xs rounded ${
                                  request.status === 'active'
                                    ? 'bg-green-100 text-green-800 cursor-not-allowed'
                                    : 'bg-green-100 text-green-800 hover:bg-green-200'
                                }`}
                              >
                                Active
                              </button>
                              <button
                                onClick={() => handleStatusChange(request._id, 'completed')}
                                disabled={request.status === 'completed' || updating}
                                className={`px-2 py-1 text-xs rounded ${
                                  request.status === 'completed'
                                    ? 'bg-blue-100 text-blue-800 cursor-not-allowed'
                                    : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                }`}
                              >
                                Completed
                              </button>
                            </div>
                            
                            {request.status === 'active' && !receipts[request._id] && (
                              <Link
                                to={`/admin/packages/generate-receipt-manually?requestId=${request._id}`}
                                className="px-2 py-1 text-xs rounded bg-indigo-100 text-indigo-800 hover:bg-indigo-200 flex items-center"
                              >
                                <FiFileText className="mr-1" /> Generate Receipt
                              </Link>
                            )}
                            {request.status === 'completed' && !receipts[request._id] && (
                              <Link
                                to={`/admin/packages/generate-receipt-manually?requestId=${request._id}`}
                                className="px-2 py-1 text-xs rounded bg-indigo-100 text-indigo-800 hover:bg-indigo-200 flex items-center"
                              >
                                <FiFileText className="mr-1" /> Generate Receipt
                              </Link>
                            )}
                            {receipts[request._id] && (
                              <Link
                                to={`/admin/packages/view-receipt?receiptId=${receipts[request._id]._id}`}
                                className="px-2 py-1 text-xs rounded bg-green-100 text-green-800 hover:bg-green-200 flex items-center"
                              >
                                <FiEye className="mr-1" /> View Receipt
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No package requests found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShowAllPackageApproval;
